import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { ViewOnlyBadge } from 'components/blocks/view-only-badge';
import { VisibilityBadge } from 'components/blocks/visibility-field';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import { Skeleton } from 'components/ui/skeleton';
import { useIsOwner } from 'hooks/use-is-owner';
import { AddPromptModal } from './components/add-prompt-modal';
import { PromptRow } from './components/prompt-row';
import { configureCardGridClassName } from 'constant/configure-layout';
import { usePromptFolder } from './hooks/use-prompts';
import type { PromptItem } from './types';

function PromptListSkeleton() {
  return (
    <div className={configureCardGridClassName}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Card
          key={index}
          className="min-w-0 overflow-hidden rounded-lg border border-grey-50 bg-white shadow-none dark:border-border dark:bg-card"
        >
          <div className="flex h-full flex-col gap-3 p-4 sm:p-6">
            <div className="flex w-full items-center justify-between">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-6 w-6 rounded" />
            </div>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
            <div className="mt-auto flex gap-2">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function PromptsFolderPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { folderId = '' } = useParams();
  const [search, setSearch] = useState('');
  const [isAddPromptOpen, setIsAddPromptOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<PromptItem | null>(null);

  const { data, isLoading, isFetching } = usePromptFolder(folderId, search);
  const folder = data?.folder;
  const prompts = data?.prompts || [];
  const canEdit = useIsOwner(folder);
  const folderDisplayName = folder?.isDefault
    ? t('PRIVATE') || folder.name
    : folder?.name || t('PRIVATE') || 'Folder';
  const isFolderLoading = isLoading || (isFetching && !folder);

  const openCreatePrompt = () => {
    if (!canEdit) return;
    setEditingPrompt(null);
    setIsAddPromptOpen(true);
  };

  const openEditPrompt = (prompt: PromptItem) => {
    setEditingPrompt(prompt);
    setIsAddPromptOpen(true);
  };

  if (!isLoading && !isFetching && !folder) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto w-fit gap-1.5 rounded-lg border border-border bg-white px-2.5 py-1.5 text-base text-foreground transition-colors hover:bg-grey-100 hover:text-foreground dark:bg-card dark:hover:bg-accent"
          onClick={() => navigate('/prompts')}
        >
          <ArrowLeft className="h-4 w-4" />
          {t('BACK_TO_PROMPT_LIBRARY') || 'Back to prompt library'}
        </Button>
        <div className="rounded-xl border border-dashed border-border/70 px-6 py-14 text-center">
          <p className="text-lg font-semibold">{t('FOLDER_NOT_FOUND') || 'Folder not found'}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('FOLDER_NOT_FOUND_DESCRIPTION') ||
              'This folder may have been deleted or you do not have access to it.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ConfigurationLayout>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden py-1">
        <div className="flex shrink-0 flex-col gap-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button
              variant="ghost"
              size="sm"
              className="h-auto w-fit gap-1.5 rounded-lg border border-border bg-white px-2.5 py-1.5 text-base text-foreground transition-colors hover:bg-grey-100 hover:text-foreground dark:bg-card dark:hover:bg-accent"
              onClick={() => navigate('/prompts')}
            >
              <ArrowLeft className="h-4 w-4" />
              {t('BACK_TO_PROMPT_LIBRARY') || 'Back to prompt library'}
            </Button>
            {isFolderLoading ? (
              <div
                className="flex w-full max-w-[240px] shrink-0 flex-col gap-1.5 rounded-lg border border-border p-3 sm:ml-auto"
                aria-hidden
              >
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-3 w-full" />
              </div>
            ) : !canEdit ? (
              <ViewOnlyBadge
                className="max-w-[240px] sm:ml-auto"
                description={
                  t('YOU_ARE_NOT_THE_OWNER_OF_THIS_FOLDER') ||
                  'You are not the owner of this folder.'
                }
              />
            ) : (
              <Button onClick={openCreatePrompt} disabled={!folder} className="shrink-0 sm:ml-auto">
                <Plus className="h-4 w-4" />
                {t('ADD_PROMPT') || 'Add prompt'}
              </Button>
            )}
          </div>

          <div className="flex min-w-0 items-center gap-2">
            <h1 className="min-w-0 truncate text-2xl font-bold tracking-tight">{folderDisplayName}</h1>
            {folder ? <VisibilityBadge value={folder.visibility} className="shrink-0" /> : null}
          </div>

          <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center p-[3px]">
              <Search className="h-[15px] w-[15px] text-grey-400" />
            </div>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('SEARCH_PROMPTS_IN_THIS_FOLDER') || 'Search prompts in this folder'}
              className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.2] shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
            />
          </div>
        </div>

        <div className="mt-6 min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-6">
          {(isLoading || isFetching) && <PromptListSkeleton />}

          {!isLoading && !isFetching && prompts.length === 0 && (
            <div className="rounded-xl border border-dashed border-border/70 px-6 py-14 text-center">
              <p className="text-lg font-semibold">{t('NO_PROMPTS_YET') || 'No prompts yet'}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {canEdit
                  ? t('ADD_FIRST_PROMPT_DESCRIPTION') ||
                  'Add your first prompt to start building reusable templates for chat.'
                  : t('NO_PROMPTS_IN_FOLDER_VIEW_ONLY') ||
                  'This folder has no prompts yet.'}
              </p>
              {canEdit ? (
                <Button className="mt-4" onClick={openCreatePrompt} disabled={!folder}>
                  <Plus className="h-4 w-4" />
                  {t('ADD_PROMPT') || 'Add prompt'}
                </Button>
              ) : null}
            </div>
          )}

          {!isLoading && !isFetching && prompts.length > 0 && (
            <div className={configureCardGridClassName}>
              {prompts.map((prompt) => (
                <PromptRow
                  key={prompt.id}
                  prompt={prompt}
                  ownerId={folder?.ownerId}
                  onEdit={openEditPrompt}
                />
              ))}
            </div>
          )}
        </div>

        <AddPromptModal
          open={isAddPromptOpen}
          onOpenChange={setIsAddPromptOpen}
          folder={folder || null}
          prompt={editingPrompt}
        />
      </div>
    </ConfigurationLayout>
  );
}

